/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import React, {FocusEventHandler, KeyboardEventHandler, MouseEventHandler} from 'react';
import {Select} from "./select";
import {ControlContext} from "../util/controlContext";
import {
    hasHover, isNull, stringContains, stringStartsWith, voidFunction
} from "../util/controlUtil";
import {ChangeFunction, SelectItemShape} from "../util/interfaces";
import { generateComponentId } from '../util/componentUtil';

export const SelectDefaultProps = {
    error: '',
    placeholder: '',
    containerClass: '',
    inputContainerClass: '',
    labelClass: '',
    itemContainerClass: '',
    itemClass: '',
    disabled: false,
    selectOnly: false,
    readOnly: false,
    fullWidth: false,
    multiple: false,
    label: '',
    unControlled: false,
    itemsHeight: 'fit-content',
    mobileNative: false,
    itemsPosition: 'absolute',
    ignoreScroll: false,
};

export interface SelectProps {
    onSelect?: any;
    onMouseEnter?: MouseEventHandler;
    onMouseLeave?: MouseEventHandler;
    onBlur?: FocusEventHandler;
    onFocus?: FocusEventHandler;
    onClick?: MouseEventHandler;
    onChangeSearch?: ChangeFunction;
    onEnter?: KeyboardEventHandler;
    renderItems?: Function;
    error: string | null;
    id?: string;
    placeholder: string | null;
    value: string | string[] | number | number[] | null;
    containerClass: string;
    inputContainerClass: string;
    labelClass: string;
    itemContainerClass: string;
    itemClass: string;
    baseColor?: string;
    itemsHeight: string;
    itemsPosition: 'relative' | 'absolute' | 'fixed' | 'sticky';
    ignoreScroll: boolean;
    label: string;
    disabled: boolean;
    selectOnly: boolean;
    readOnly: boolean;
    fullWidth: boolean;
    items: SelectItemShape[];
    multiple: boolean;
    unControlled: boolean;
    mobileNative: boolean;
    forceDisplayItems?: boolean;
}

interface State {
    searchValue: string;
    isHovered: boolean;
    isFocused: boolean;
    itemsOpen: boolean;
    hoverItem: string | number | null;
    value: string | string[] | number | number[] | null;
    itemsCoords: {
        top: number;
        scrollTop: number;
        left: number;
    };
}

// noinspection JSUnusedGlobalSymbols
export default class SelectController extends React.Component<SelectProps, State> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    static defaultProps = SelectDefaultProps;

    state: State = {
        searchValue: '',
        isHovered: false,
        isFocused: false,
        itemsOpen: false,
        hoverItem: null,
        value: this.props.value,
        itemsCoords: {
            top: 0,
            scrollTop: 0,
            left: 0,
        }
    };

    private id = generateComponentId(this.props.id);
    private isScrolling: boolean = false;

    hoverTimeout: number | null = null;

    onSelect = (item: SelectItemShape) => (evt: MouseEvent) => {
        evt.stopPropagation();
        const {onSelect, multiple, value, selectOnly} = this.props, itemValue = item.value;
        if (item.selectable === null || item.selectable === undefined || item.selectable) {
            if (onSelect !== undefined && onSelect !== null)
                if (multiple) {
                    const selected: (string | number)[] = (value as (string | number)[]);
                    if (selected.includes(itemValue)) selected.splice(selected.indexOf(itemValue), 1);
                    else selected.push(itemValue);
                    if (!selectOnly) this.getInput()?.focus();
                    this.setState({value: selected as (string | number[])});
                    onSelect(selected, evt);
                } else {
                    this.setState({value: itemValue});
                    onSelect(itemValue, evt);
                }
            this.setState({itemsOpen: multiple, isFocused: false});
        }
    };

    onSelectNative = (evt: React.ChangeEvent<HTMLSelectElement>) => {
        const value = evt.target.options[evt.target.selectedIndex].value, {onSelect} = this.props;
        if (onSelect) onSelect(value, evt);
    };

    onClear = (evt: any) => {
        evt.stopPropagation();
        const {multiple, onSelect} = this.props;
        if (onSelect !== null && onSelect !== undefined) {
            if (multiple) onSelect([]);
            else onSelect(null);
        }
    };

    onMouseEnter = (evt: any) => {
        const {onMouseEnter} = this.props;
        this.setState({isHovered: true});
        if (onMouseEnter !== undefined && onMouseEnter !== null)
            onMouseEnter(evt);
    };

    onMouseLeave = (evt: any) => {
        const {onMouseLeave} = this.props;
        this.setState({isHovered: false});
        if (onMouseLeave !== undefined && onMouseLeave !== null)
            onMouseLeave(evt);
    };

    onMouseEnterItem = (item: SelectItemShape) => () => {
        const value = item.value;
        if (item.selectable === null || item.selectable === undefined || item.selectable) {
            if (this.hoverTimeout !== null)
                window.clearTimeout(this.hoverTimeout);
            this.setState({hoverItem: value});
        }
    };

    onMouseLeaveItem = () => {
        this.hoverTimeout = window.setTimeout(() => {
            this.setState({hoverItem: null});
        }, 200)
    };

    onSearch = (value: string) => {
        const {onChangeSearch} = this.props;
        this.setState({searchValue: value});
        if (onChangeSearch) onChangeSearch(value);
    };

    onFocus = (evt: any) => {
        const {onFocus, disabled, readOnly} = this.props;
        if (disabled || readOnly) return;
        this.setItemsContainerCoords(() => {
            this.setState({isFocused: true, itemsOpen: true});
        });
        if (onFocus !== undefined && onFocus !== null)
            onFocus(evt);
    };

    onClickLabel = (evt: any) => {
        const {onClick, disabled, readOnly, selectOnly} = this.props;
        if (disabled || readOnly) return;
        if (selectOnly)
            this.setItemsContainerCoords(() => {
                this.setState({itemsOpen: !this.state.itemsOpen});
            });
        else {
            evt.preventDefault();
            this.getInput()?.focus();
        }
        if (onClick !== undefined && onClick !== null)
            onClick(evt);
    };

    onClick = (evt: React.MouseEvent) => {
        const {onClick, selectOnly, disabled, readOnly} = this.props;
        if (disabled || readOnly) return;
        if (!selectOnly)
            this.getInput()?.focus();
        if (onClick !== undefined && onClick !== null)
            onClick(evt);
    };

    onWindowClick = () => {
        const container = this.getContainer();
        if (container === null || !hasHover(container))
            this.setState({itemsOpen: false, isFocused: false, searchValue: ''});
    };

    // noinspection JSUnusedGlobalSymbols
    /**
     * Function for external component to close the item container.
     */
    closeItems = () => {
        this.setState({itemsOpen: false, isFocused: false});
    };

    setItemsContainerCoords = (callback: () => void = voidFunction) => {
        const container = document.getElementById(this.id);
        if (container) {
            const {ignoreScroll} = this.props, box = container.getBoundingClientRect();
            let top = box.top + box.height, scrollTop = ignoreScroll ? 0 : window.scrollY;
            if (!ignoreScroll) top += window.scrollY;
            this.setState({
                itemsCoords: {
                    top,
                    scrollTop,
                    left: box.left,
                }
            }, callback);
        } else callback();
    };

    getInput = (): HTMLElement | null => document.getElementById(this.id + '-input');

    getItems = (): SelectItemShape[] => {
        const {searchValue} = this.state, {items, forceDisplayItems} = this.props;
        if (isNull(searchValue) || forceDisplayItems) return items;
        return this.getStartItems().concat(this.getContainItems());
    };

    getStartItems = (): SelectItemShape[] => {
        const {items} = this.props, {searchValue} = this.state;
        return items.filter(item => stringStartsWith(item.label.toString(), searchValue));
    };

    getContainItems = (): SelectItemShape[] => {
        const {items} = this.props, {searchValue} = this.state;
        return items.filter(item => {
            const itemLabel = item.label.toString();
            return !stringStartsWith(itemLabel, searchValue) && stringContains(itemLabel, searchValue);
        });
    };

    getContainer = (): HTMLElement | null => document.getElementById(this.id);

    scrollListener = () => {
        if (!this.isScrolling) {
            window.requestAnimationFrame(() => {
                const {itemsCoords} = this.state;
                this.setState({itemsCoords: Object.assign({}, itemsCoords, {scrollTop: window.scrollY})});
                this.isScrolling = false;
            });
            this.isScrolling = true;
        }
    };

    addClickListener = () => {
        window.addEventListener('click', this.onWindowClick);
    };

    removeClickListener = () => {
        window.removeEventListener('click', this.onWindowClick);
    };

    addScrollListener = () => {
        if (this.props.itemsPosition === 'fixed' && !this.props.ignoreScroll)
            document.addEventListener('scroll', this.scrollListener);
    };

    removeScrollListener = () => {
        document.removeEventListener('scroll', this.scrollListener);
    };

    getBaseColor = (): string => {
        const {baseColor} = this.props;
        if (baseColor !== undefined && baseColor !== null) return baseColor;
        // noinspection JSDeprecatedSymbols
        return this.context.baseColor;
    };

    componentDidMount = () => {
        this.addClickListener();
    };

    componentDidUpdate = (prevProps: SelectProps, prevState: State) => {
        const {itemsOpen} = this.state, prevOpen = prevState.itemsOpen;
        if (itemsOpen && !prevOpen)
            // Items are opened
            this.addScrollListener();
        else if (!itemsOpen && prevOpen)
            // Items are closed
            this.removeScrollListener();
    };

    componentWillUnmount = () => {
        this.removeClickListener();
        this.removeScrollListener();
    };

    render = () => {
        const {
            error, placeholder, containerClass, itemContainerClass, inputContainerClass, labelClass,
            itemClass, itemsHeight, disabled, selectOnly, readOnly, label, renderItems, fullWidth, multiple, value,
            unControlled, mobileNative, itemsPosition, onEnter
        } = this.props, {searchValue, isHovered, isFocused, itemsOpen, hoverItem, itemsCoords} = this.state;
        const selectValue = unControlled ? this.state.value : value;
        return <Select onSelect={this.onSelect} onMouseEnter={this.onMouseEnter}
                       hoverItem={hoverItem} onMouseLeave={this.onMouseLeave} onSearch={this.onSearch}
                       onFocus={this.onFocus} onClick={this.onClick} error={error} placeholder={placeholder}
                       containerClass={containerClass} inputContainerClass={inputContainerClass}
                       labelClass={labelClass} itemContainerClass={itemContainerClass} itemClass={itemClass}
                       baseColor={this.getBaseColor()} searchValue={searchValue} id={this.id}
                       itemsHeight={itemsHeight} disabled={disabled} selectOnly={selectOnly} readOnly={readOnly}
                       isHovered={isHovered} isFocused={isFocused} itemsOpen={itemsOpen} items={this.getItems()}
                       label={label} onMouseEnterItem={this.onMouseEnterItem} renderItems={renderItems}
                       onMouseLeaveItem={this.onMouseLeaveItem} onClickLabel={this.onClickLabel}
                       fullWidth={fullWidth} multiple={multiple} value={selectValue} onClear={this.onClear}
                       unControlled={unControlled} onSelectNative={this.onSelectNative} mobileNative={mobileNative}
                       itemsCoords={itemsCoords} itemsPosition={itemsPosition} onEnter={onEnter}/>;
    };
}